import { useCallback, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TaskListItem, UpdateTaskDto } from '@myapp/contract'
import { DefaultDurations } from '../../../shared/lib/blockRanges'
import { taskApi, taskKeys } from '../../../entities/task'
import { useTaskDrag } from './useTaskDrag'

type HasCollisionFn = (iso: string, dur: DefaultDurations, techId: string, excludeId?: string, excludeTaskId?: number) => boolean

interface Options {
  dayIso: string
  hasCollision?: HasCollisionFn
  technicians?: { technicianId: string; workingDays: number[]; shiftStart: string | null; shiftEnd: string | null }[]
}

export function useTaskReschedule({ dayIso, hasCollision, technicians }: Options) {
  const queryClient = useQueryClient()
  const lastStatusRef = useRef<TaskListItem['status'] | null>(null)

  const dropMutation = useMutation({
    mutationFn: ({ taskId, ...dto }: { taskId: number } & UpdateTaskDto) =>
      taskApi.update(taskId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() })
    },
  })

  const handleTaskDropped = useCallback(
    async (taskId: number, update: { plannedStart: string; assignedTechnicianId: number }) => {
      const status = lastStatusRef.current
      const dto: UpdateTaskDto = status === 'COMPLETED'
        ? { ...update, actualStart: update.plannedStart }
        : update
      await dropMutation.mutateAsync({ taskId, ...dto })
    },
    [dropMutation],
  )

  const confirmBeforeDrop = useCallback(async (status: TaskListItem['status']) => {
    lastStatusRef.current = status
    if (status !== 'COMPLETED') return true
    if (typeof window === 'undefined') return true
    return window.confirm('Diese Aufgabe ist abgeschlossen. Wirklich verschieben?')
  }, [])

  const { dragState: taskDragState, startDrag: startTaskDrag } = useTaskDrag({
    dayIso,
    hasCollision,
    technicians,
    onTaskDropped: handleTaskDropped,
    confirmBeforeDrop,
  })

  return { taskDragState, startTaskDrag }
}
